import React, {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import {
  Modal, Input, Loader, Segment, Button, Message, Image, List,
} from 'semantic-ui-react';
import { connect } from 'react-redux';
import api from 'api';
import util from 'utils/utils';

import IdeasImage from 'src/images/ideas.png';
import { ThemedButton } from 'components/lib/UI';

function ChallengeChooser({
  trigger, ignores, createNewAction, instructions, onComplete, user, controlled, isOpen, onClose, messageProps,
}) {
  const [challenges, setChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [challengeSearchState] = useState({
    page: 1,
    limit: 10,
    query: '',
  });
  const userId = user?._id;

  useEffect(() => {
    if (open && userId) {
      setLoading(true);
    }
    api.users.getChallenges(userId, challengeSearchState, (data) => {
      setLoading(false);
      let { challenges: newChallenges } = data;
      if (ignores?.length > 0) newChallenges = newChallenges.filter((c) => ignores.indexOf(c._id) === -1);
      setChallenges(newChallenges);
    });
  }, [open, userId, ignores, challengeSearchState]);

  const onClickChallenge = useCallback((challenge) => {
    if (selectedChallenge?._id === challenge._id) setSelectedChallenge(null);
    else setSelectedChallenge(challenge);
  }, [selectedChallenge]);

  const onChoose = useCallback((challenge) => {
    onComplete(challenge);
    setSelectedChallenge(null);
  }, [onComplete]);

  const exp = useMemo(() => new RegExp(search, 'i'), [search]);
  const filteredChallenges = useMemo(() => (search
    ? challenges.filter((i) => i.name?.match(exp) || i.ownerChallenge?.name?.match(exp))
    : challenges),
  [search, challenges, exp]);

  return (
    <>
      {controlled ? null : (
        <>
        {trigger
          ? React.cloneElement(trigger, { onClick: () => setOpen(!open) })
          : <Button size='small' primary icon='lightbulb' content='Choose a challenge' onClick={() => setOpen(!open)} />
          }
        </>
      )}

      <Modal open={controlled ? isOpen : open} size='tiny'
        onClose={() => {
          if (controlled) onClose();
          else setOpen(false);
        }}
      >
        <Modal.Header>
          <h3>Choose a challenge</h3>
          {instructions && <h5 style={{ marginTop: 0 }}>{instructions}</h5>}
        </Modal.Header>
        <Modal.Content>
          {messageProps && <Message {...messageProps} />}
          {loading && <Loader active />}
          {!loading && !challenges?.length
            && <Segment placeholder textAlign='center'>
              <img src={IdeasImage} style={{
                maxHeight: 100, maxWidth: '80%', display: 'block', margin: '5px auto',
              }} alt='No challenges yet' />
              <h4>No challenges are currently available</h4>
              {util.canCreateChallenges(user) && createNewAction
                && <Button primary onClick={createNewAction} content='Create a new challenge' icon='plus' />
              }
            </Segment>
          }
          {challenges?.length > 0
            && <>
              <div style={{ textAlign: 'right', marginBottom: 20 }}>
                <Input autoFocus icon='search' placeholder='Search for a challenge...' value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              {filteredChallenges.length > 0
                ? (
                  <List verticalAlign='middle' size='large' selection>
                    {filteredChallenges.map((challenge) => (
                      <List.Item
                        key={challenge._id}
                        onClick={() => onClickChallenge(challenge)}
                        style={{ cursor: 'pointer' }}
                        active={selectedChallenge?._id === challenge._id}
                      >
                        <Image avatar src={challenge.bannerImageUrl} />
                        <List.Content>
                          <List.Header>{challenge.name}</List.Header>
                        </List.Content>
                      </List.Item>
                    ))}
                  </List>
                ) : (
                  <Segment placeholder textAlign='center'>
                    <h4>No challenges match your search term</h4>
                    <p>Adjust your query and try again.</p>
                  </Segment>
                )
              }
            </>
          }
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => (controlled ? onClose() : setOpen(false))} content='Cancel' />
          {util.canCreateChallenges(user) && challenges?.length > 0 && createNewAction
            && <ThemedButton primary onClick={createNewAction} content='Create a new challenge instead' icon='plus' />
          }
          {selectedChallenge
            ? (
              <ThemedButton
                primary
                onClick={() => onChoose(selectedChallenge)}
                content='Choose this challenge'
                icon='check'
              />
            ) : null
          }
        </Modal.Actions>
      </Modal>
    </>
  );
}

const mapStateToProps = (state) => ({ user: state.user });
export default connect(mapStateToProps)(ChallengeChooser);
